<script>
import AuthBase from '@/components/auth/AuthBase'
import SaveDialog from '@/components/dialogs/SaveDialog.vue'
import ACLDialog from '@/components/auth/ACLDialog'
import HazardAppearanceSettings from '@/components/hazard/HazardAppearanceSettings'
import HazardTableSettings from '@/components/hazard/HazardTableSettings'
import TransferMenu from '@/components/TransferMenu.vue'
import OptionsDropdown from '@/components/OptionsDropdown.vue'

export default {
  mixins: [AuthBase],

  components: {
    ACLDialog,
    SaveDialog,
    HazardAppearanceSettings,
    HazardTableSettings,
    TransferMenu,
    OptionsDropdown,
  },

  props: {
    report: {
      type: Object,
      default: null,
    },
    error: {
      type: String,
      default: null,
    },
    filters: {
      type: Object,
      required: true,
    },
    allowSingleMode: {
      type: Boolean,
      default: false,
    },
    singleMode: {
      type: Boolean,
      default: false,
    },
    showImages: {
      type: Boolean,
      default: false,
    },
    profiles: {
      type: Array,
      default: () => [],
    },
    hierarchy: {
      type: Array,
      default: () => [],
    },
    checkedStructures: {
      type: Array,
      default: () => [],
    },
    downloading: {
      type: Boolean,
      default: false,
    },
    exportItems: {
      type: Array,
      default: () => [],
    },
    selectedStructures: {
      type: Array,
      default: () => [],
    },
    recordsForTransfer: {
      type: Array,
      default: () => [],
    },
    hazardUrl: {
      type: String,
      required: true,
    },
    defaultVTooltipProps: {
      type: Object,
      required: true,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    toolbarClass: {
      type: [String, Array, Object],
      default: '',
    },
  },

  computed: {
    filtersModel: {
      get() {
        return this.filters
      },
      set(value) {
        this.$emit('update:filters', value)
      },
    },
    singleModeModel: {
      get() {
        return this.singleMode
      },
      set(value) {
        this.$emit('update:singleMode', value)
      },
    },
    downloadingModel: {
      get() {
        return this.downloading
      },
      set(value) {
        this.$emit('update:downloading', value)
      },
    },
    showImagesModel: {
      get() {
        return this.showImages
      },
      set(value) {
        this.$emit('update:showImages', value)
      },
    },
  },
}
</script>

<template>
  <div
    class="relative d-flex flex-wrap flex-grow-1 gap-2 align-center justify-end"
    :class="toolbarClass"
  >
    <div class="d-flex align-center gap-1">
      <v-tooltip
        top
        dense
        v-bind="defaultVTooltipProps"
        :open-on-focus="false"
        :open-on-click="false"
      >
        <template #activator="{ on }">
          <v-text-field
            v-on="on"
            v-model="filtersModel.chemicalStr"
            hide-details
            dense
            class="grow"
            style="max-width: 10rem"
          >
            <template #label>
              <v-icon
                small
                left
              >
                mdi-magnify
              </v-icon>
              <span class="text-muted font-weight-light small-caption">Search chemical</span>
            </template>

            <template #append>
              <v-icon
                v-if="filtersModel.chemicalStr"
                small
                class="cursor-pointer"
                @click="filtersModel.chemicalStr = ''"
              >
                mdi-close-circle
              </v-icon>
            </template>
          </v-text-field>
        </template>

        Search by Name, CASRN or DTXSID
      </v-tooltip>

      <v-spacer />

      <HazardAppearanceSettings
        :filters.sync="filtersModel"
        :allow-single-mode="allowSingleMode"
        :single-mode.sync="singleModeModel"
        :show-images.sync="showImagesModel"
        button-title="mdi-tune-variant"
        title="Display settings"
        max-width="420"
      />

      <HazardTableSettings
        :filters.sync="filtersModel"
        :profiles="profiles"
        :hierarchy="hierarchy"
        button-title="mdi-table-cog"
        title="Table settings"
        max-width="600"
      />
    </div>

    <div class="d-flex align-center gap-1">
      <v-btn-toggle dense>
        <v-btn
          id="hazard-report-select-all-button"
          aria-label="Select All"
          @click="$emit('select-all')"
        >
          <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
        </v-btn>

        <v-tooltip
          top
          v-bind="defaultVTooltipProps"
          activator="#hazard-report-select-all-button"
        >
          Select All
        </v-tooltip>
        <v-tooltip
          top
          v-bind="defaultVTooltipProps"
          activator="#hazard-report-select-none-button"
        >
          Select none
        </v-tooltip>
        <v-btn
          id="hazard-report-select-none-button"
          aria-label="Select None"
          @click="$emit('clear-selection')"
        >
          <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
        </v-btn>
      </v-btn-toggle>

      <OptionsDropdown
        :items="exportItems"
        title="Export to SDF, Excel, Excel (split)"
        :loading="downloading"
        :disabled="!report"
      />

      <v-btn-toggle
        v-if="isAuthenticated || (report && report.id && canACL(report))"
        dense
      >
        <SaveDialog
          v-if="isAuthenticated"
          :api-url="`${hazardUrl}/reports`"
          :button-title="report && report.id ? 'mdi-content-save-edit-outline' : 'mdi-content-save-outline'"
          :item="report"
          :title="report && report.id ? 'Edit Report' : 'Save Report'"
          @itemCreated="$emit('report-saved', $event)"
          @itemUpdated="$emit('report-updated', $event)"
        />

        <ACLDialog
          v-if="report && report.id && canACL(report)"
          :api-url="hazardUrl"
          :item="report"
          button-title="mdi-account-key"
          title="Access Control"
          type="reports"
          @aclUpdated="$emit('acl-updated', $event)"
        />
      </v-btn-toggle>

      <TransferMenu
        :records="recordsForTransfer"
        only-icon
      />
    </div>
  </div>
</template>

<style lang="scss">
@media (max-width: 1400px) {
  .hazard-report-search-field {
    order: 10;
  }
}
</style>
